import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface PromoConfig {
  active: boolean;
  label: string;
  expiresAt: string;
  pages: string[];
}

let cachedConfig: PromoConfig | null = null;
let cacheExpiry = 0;

async function loadPromoConfig(): Promise<PromoConfig> {
  if (cachedConfig && Date.now() < cacheExpiry) return cachedConfig;

  const keys = ['referral_promo_active', 'referral_promo_label', 'referral_promo_expires_at', 'referral_promo_pages'];
  const { data } = await supabase.from('platform_settings').select('key, value').in('key', keys);
  const map: Record<string, string> = {};
  (data ?? []).forEach(r => { map[r.key] = String(r.value); });

  let pages: string[] = [];
  try { pages = JSON.parse(map['referral_promo_pages'] ?? '[]'); } catch { pages = []; }

  cachedConfig = {
    active:    map['referral_promo_active'] === 'true',
    label:     map['referral_promo_label']  ?? 'Ganhe 1 mês grátis',
    expiresAt: map['referral_promo_expires_at'] ?? '',
    pages,
  };
  cacheExpiry = Date.now() + 5 * 60 * 1000; // 5 min cache
  return cachedConfig;
}

/** Place on any service page. Pass `page` matching one of the admin-configured slugs. */
export function ReferralBanner({ page }: { page: string }) {
  const { profile } = useAuth();
  const [config,    setConfig]    = useState<PromoConfig | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    loadPromoConfig().then(setConfig);
  }, []);

  if (!profile || !config) return null;
  if (!config.active) return null;
  if (config.expiresAt && new Date(config.expiresAt) < new Date()) return null;
  if (!config.pages.includes(page)) return null;
  if (dismissed) return null;

  const storageKey = `professya_referral_banner_${profile.id}_${page}`;
  if (typeof window !== 'undefined' && localStorage.getItem(storageKey) === '1') return null;

  function dismiss() {
    localStorage.setItem(storageKey, '1');
    setDismissed(true);
  }

  return (
    <div style={{
      marginTop: '1.25rem',
      padding: '0.75rem 1rem',
      borderRadius: 'var(--radius-card)',
      border: '1px solid rgba(31,78,95,0.3)',
      background: 'rgba(31,78,95,0.06)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.75rem',
      flexWrap: 'wrap',
    }}>
      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.55, flex: 1 }}>
        🎁 <strong style={{ color: 'var(--text-primary)' }}>Indique uma amiga e {config.label}.</strong>{' '}
        Quando ela assinar, você recebe automaticamente.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
        <Link
          to="/referral"
          className="btn btn-sm"
          style={{ textDecoration: 'none', background: 'rgba(31,78,95,0.12)', color: 'var(--petrol)', border: '1px solid rgba(31,78,95,0.3)', whiteSpace: 'nowrap' }}
        >
          Ver meu link →
        </Link>
        <button
          onClick={dismiss}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem', padding: '0.2rem 0.4rem' }}
          aria-label="Dispensar"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
